/**
 * [FACTORY-293] Conversation-scoped persistence for queued (typed-while-busy)
 * follow-up messages, so a refresh / tab-crash / nav-away never silently
 * discards them. Keyed by conversation id, so restored items can never surface
 * in the wrong chat. Cleared once the queue actually drains.
 *
 * Pure + DOM-guarded (mirrors `lib/theme.ts`'s `globalThis.localStorage?`) so it
 * unit-tests under `bun:test` and no-ops under SSR/prerender. Never throws.
 */

export interface QueuedChatMeta {
  slashCommand?: { trigger: string; label: string }
}

export interface QueueEntry {
  content: string
  meta?: QueuedChatMeta
}

const keyFor = (convId: string): string => `agent:queue:${convId}`

export function loadQueue(convId: string): QueueEntry[] {
  const raw = globalThis.localStorage?.getItem(keyFor(convId))
  if (!raw) {
    return []
  }
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter((e): e is QueueEntry => e && typeof e.content === 'string')
  } catch {
    return []
  }
}

export function saveQueue(convId: string, queue: QueueEntry[]): void {
  const store = globalThis.localStorage
  if (!store) {
    return
  }
  // Empty ⇒ remove the key so "cleared" is observable (and doesn't linger).
  if (queue.length === 0) {
    store.removeItem(keyFor(convId))
    return
  }
  try {
    store.setItem(keyFor(convId), JSON.stringify(queue))
  } catch {
    // Quota / disabled storage — best-effort, never break the send path.
  }
}

export function clearQueue(convId: string): void {
  globalThis.localStorage?.removeItem(keyFor(convId))
}
